import React, { useCallback, useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'

import { fetchItem } from './helpers'
import { useOnOutsideClick } from 'hooks/useOnOutsideClick'

import { DEAL_TYPE, PERSON_TYPE, COMPANY_TYPE, PROJECT_TYPE, PROJECT_MILESTONE_TYPE } from './constants'
import { BusinessCardProvider } from './context'

import Person from './components/Person'
import Deal from './components/Deal'
import DealPeopleCarousel from './components/DealPeopleCarousel'

import Company from './components/Company'
import Project from './components/Project'
import ProjectMilestone from './components/ProjectMilestone'
import Header from './components/Header'

import { Sections, CardWrapper, PopoverWrapper } from './components/StyledComponents'
import CustomFields from './components/CustomFields'

const BusinessCard = ({
  avatarUpdateCallback,
  currentUser,
  element,
  hideOnLeave,
  highlighter,
  isPrimary,
  itemData,
  itemId,
  markPrimary,
  onClick,
  onClose,
  onDeleteLinkClicked,
  onMarkPrimary,
  onItemFetched,
  setIsOver,
  showAvatar,
  showAdditionalFields,
  showAssociationsCount,
  showDeleteLink,
  showEditLink,
  showTooltips,
  showNameAsLink,
  showOnlyFields,
  showPeopleCarousel,
  type,
  showEmptyFields,
  showMainDivider,
  wrapped,
  attributesSectionScrollable,
  attributesSectionMaxHeight,
  selected
}) => {
  const node = useRef()
  const [item, setItem] = useState({})
  const [fetched, setFetched] = useState(false)

  const onClickOutside = (e) => {
    if (
      document.querySelector('.business-card-option')?.contains(e.target) // Business Card Option Buttons (ButtonMenu ListItems)
    ) {
      return // Keep the BusinessCard popover open if the click occurs within the BusinessCard Options
    }
    return (element && onClose) && onClose(false)
  }

  useOnOutsideClick({ node, showComponent: true, onOutsideClick: onClickOutside })

  const reloadPerson = useCallback(e => {
    const eventItemId = e?.detail?.id

    if (eventItemId !== item.id) return

    fetchItem(type, item.id).then(r => setItem(r))
  }, [item.id])

  const reloadCompany = useCallback(e => {
    const eventItemId = e?.detail?.id
    const itemCompanyId = item.company_id

    if (itemCompanyId !== eventItemId) return

    fetchItem(type, item.id).then(r => setItem(r))
  }, [item.company_id])

  const reloadProject = useCallback(e => {
    const eventItemId = e?.detail?.id

    if (eventItemId !== item.id) return

    fetchItem(type, item.id).then(r => setItem(r))
  }, [item.id])

  const reloadProjectMilestone = useCallback(e => {
    const eventItemId = e?.detail?.id

    if (eventItemId !== item.id) return

    fetchItem(type, item.id).then(r => setItem(r))
  }, [item.id])

  useEffect(() => {
    window.addEventListener('person:update', reloadPerson)
    window.addEventListener('company:update', reloadCompany)
    window.addEventListener('project:update', reloadProject)
    window.addEventListener('project_milestone:update', reloadProjectMilestone)

    return () => {
      window.removeEventListener('person:update', reloadPerson)
      window.removeEventListener('company:update', reloadCompany)
      window.removeEventListener('project:update', reloadProject)
      window.removeEventListener('project_milestone:update', reloadProjectMilestone)
    }
  }, [item.id, item.company_id])

  useEffect(() => {
    if (itemData) {
      setItem(itemData)
      setFetched(true)
    } else {
      fetchItem(type, itemId).then((r) => {
        setItem(r)
        setFetched(true)
      })
    }
  }, [itemData, type, itemId])

  useEffect(() => {
    if (fetched) {
      onItemFetched && onItemFetched()
    }
  }, [fetched])

  if (!fetched) return null

  return (
    <BusinessCardProvider
      avatarUpdateCallback={avatarUpdateCallback}
      currentUser={currentUser || window.User}
      element={element}
      attributesSectionScrollable={attributesSectionScrollable}
      hideOnLeave={hideOnLeave}
      highlighter={highlighter}
      isPrimary={isPrimary}
      linkifyName={showNameAsLink}
      markPrimary={markPrimary}
      onClick={onClick}
      onClose={onClose}
      onDeleteLinkClicked={onDeleteLinkClicked}
      onMarkPrimary={onMarkPrimary}
      resource={item}
      resourceId={item.id}
      resourceType={type}
      setIsOver={setIsOver}
      showAdditionalFields={showAdditionalFields}
      showAssociationsCount={showAssociationsCount}
      showDeleteLink={showDeleteLink}
      showEditLink={showEditLink}
      showTooltips={showTooltips}
      showOnlyFields={showOnlyFields}
      showPeopleCarousel={showPeopleCarousel}
      showEmptyFields={showEmptyFields}
      showMainDivider={showMainDivider}
    >
      <PopoverWrapper>
        <div ref={node}>
          <CardWrapper>

            {type === DEAL_TYPE && showPeopleCarousel ? (
              <DealPeopleCarousel />
            ) : (
              <>
                <Header showAvatar={showAvatar} />
                <Sections
                  wrapped={wrapped}
                  selected={selected}
                  scrollable={attributesSectionScrollable}
                  maxHeight={attributesSectionMaxHeight}
                >
                  {type === PERSON_TYPE && <Person />}
                  {type === DEAL_TYPE && <Deal />}
                  {type === COMPANY_TYPE && <Company />}
                  {type === PROJECT_TYPE && <Project />}
                  {type === PROJECT_MILESTONE_TYPE && <ProjectMilestone />}
                  <CustomFields />
                </Sections>
              </>
            )}
          </CardWrapper>
        </div>
      </PopoverWrapper>
    </BusinessCardProvider>
  )
}

BusinessCard.propTypes = {
  avatarUpdateCallback: PropTypes.func,
  currentUser: PropTypes.object,
  element: PropTypes.object,
  hideOnLeave: PropTypes.bool,
  highlighter: PropTypes.string,
  isPrimary: PropTypes.bool,
  itemData: PropTypes.object,
  itemId: PropTypes.number.isRequired,
  markPrimary: PropTypes.bool,
  onClick: PropTypes.func,
  onClose: PropTypes.func,
  onDeleteLinkClicked: PropTypes.func,
  onMarkPrimary: PropTypes.func,
  onItemFetched: PropTypes.func,
  setIsOver: PropTypes.func,
  showAvatar: PropTypes.bool,
  showAdditionalFields: PropTypes.array,
  showAssociationsCount: PropTypes.bool,
  showDeleteLink: PropTypes.bool,
  showEditLink: PropTypes.bool,
  showTooltips: PropTypes.bool,
  showNameAsLink: PropTypes.bool,
  showOnlyFields: PropTypes.array,
  showPeopleCarousel: PropTypes.bool,
  type: PropTypes.string.isRequired,
  showEmptyFields: PropTypes.bool,
  showMainDivider: PropTypes.bool,
  wrapped: PropTypes.bool,
  attributesSectionScrollable: PropTypes.bool,
  attributesSectionMaxHeight: PropTypes.string,
  selected: PropTypes.bool
}

export {
  BusinessCard,
  DEAL_TYPE,
  PERSON_TYPE,
  COMPANY_TYPE,
  PROJECT_TYPE,
  PROJECT_MILESTONE_TYPE
}
